// src/components/admin/AddDaysLeft.js
import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Button, Table, Form, InputGroup,
  Spinner, Pagination, Alert, Modal, Badge
} from 'react-bootstrap';
import {
  FaUniversity, FaSearch, FaArrowLeft, FaPlusCircle, FaUserShield, FaCalendarPlus, FaUsers, FaSort, FaSortUp, FaSortDown, FaDownload
} from 'react-icons/fa';
import { format, differenceInCalendarDays } from 'date-fns';
import './AddDaysLeft.css';

const AddDaysLeft = () => {
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null); // null = Non-College view, object = college
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Search for tiles
  const [tileSearch, setTileSearch] = useState('');

  // API Filters
  const [filterName, setFilterName] = useState('');
  const [filterUserName, setFilterUserName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterPhoneNumber, setFilterPhoneNumber] = useState('');
  const [filterAccountStatus, setFilterAccountStatus] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterActivityStatus, setFilterActivityStatus] = useState('');

  // Pagination for student list
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Extend modal
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [extraDays, setExtraDays] = useState(30);
  const [updating, setUpdating] = useState(false);
  const [extensionMode, setExtensionMode] = useState('days'); // 'days' or 'date'
  const [targetDate, setTargetDate] = useState('');

  // Fetch all colleges
  useEffect(() => {
    const fetchColleges = async () => {
      try {
        setLoading(true);
        const response = await apiService.get('/api/college');
        if (response.success && Array.isArray(response.message?.showCollege)) {
          setColleges(response.message.showCollege);
        }
      } catch (err) {
        setError('Failed to load colleges');
      } finally {
        setLoading(false);
      }
    };
    fetchColleges();
  }, []);

  const fetchSubscriptions = async () => {
    if (selectedCollege === null) return;
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (selectedCollege && selectedCollege.college_id && selectedCollege.college_id !== 'all') {
        params.collegeId = selectedCollege.college_id;
      } else if (selectedCollege && selectedCollege.college_id === 'all') {
        params.collegeId = 'all';
      }
      if (filterName) params.name = filterName;
      if (filterUserName) params.userName = filterUserName;
      if (filterEmail) params.email = filterEmail;
      if (filterPhoneNumber) params.phoneNumber = filterPhoneNumber;
      if (filterAccountStatus) params.accountStatus = filterAccountStatus;
      if (filterStatus) params.status = filterStatus;
      if (filterActivityStatus) params.activityStatus = filterActivityStatus;

      const response = await apiService.get('/api/user-admin/subscriptions', params);
      if (response.success && Array.isArray(response.message?.users)) {
        setSubscriptions(response.message.users);
      } else {
        setError('Failed to load students');
      }
    } catch (err) {
      setError('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch subscriptions when selection changes
  useEffect(() => {
    if (selectedCollege === null) return;
    fetchSubscriptions();
  }, [selectedCollege]);

  // Reset currentPage to 1 when filters or selected college changes
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterName,
    filterUserName,
    filterEmail,
    filterPhoneNumber,
    filterAccountStatus,
    filterStatus,
    filterActivityStatus,
    selectedCollege
  ]);

  // Filter tiles (colleges + Non-College)
  const filteredTiles = colleges.filter(college =>
    college.college_name?.toLowerCase().includes(tileSearch.toLowerCase()) ||
    college.college_code?.toLowerCase().includes(tileSearch.toLowerCase())
  );

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort students in the list
  const sortedStudents = React.useMemo(() => {
    let sortableStudents = [...subscriptions];

    // Then Sort
    if (sortConfig.key !== null) {
      sortableStudents.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle null/undefined values
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        // If sorting dates
        if (['start_date', 'end_date', 'last_study_date'].includes(sortConfig.key)) {
          const aTime = aVal ? new Date(aVal).getTime() : 0;
          const bTime = bVal ? new Date(bVal).getTime() : 0;
          return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
        }

        // If sorting numbers
        if (['access_count_7_days'].includes(sortConfig.key)) {
          const aNum = Number(aVal) || 0;
          const bNum = Number(bVal) || 0;
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // Default string comparison
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aStr > bStr) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableStudents;
  }, [subscriptions, tileSearch, sortConfig]);

  const totalPages = Math.ceil(sortedStudents.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedStudents.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getDaysLeft = (endDate) => {
    if (!endDate) return 0;
    const days = differenceInCalendarDays(new Date(endDate), new Date());
    return days > 0 ? days : 0;
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1 text-muted" style={{ fontSize: '0.75rem', opacity: 0.5 }} />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="ms-1 text-primary" style={{ fontSize: '0.85rem' }} />
      : <FaSortDown className="ms-1 text-primary" style={{ fontSize: '0.85rem' }} />;
  };

  const downloadCSV = () => {
    if (sortedStudents.length === 0) return;

    const headers = [
      'Name',
      'Phone Number',
      'Email',
      'Username',
      'College Name',
      'Status',
      'Account Status',
      'Activity Status',
      'Joined (Start Date)',
      'Expiration Date',
      'Last Study Date',
      'Last Lesson',
      'Access (7 Days)'
    ];

    const csvRows = [headers.join(',')];

    for (const user of sortedStudents) {
      const rowData = [
        `"${(user.name || '').replace(/"/g, '""')}"`,
        `"${(user.phone_number || '').replace(/"/g, '""')}"`,
        `"${(user.email || '').replace(/"/g, '""')}"`,
        `"${(user.user_name || '').replace(/"/g, '""')}"`,
        `"${(user.college_name || 'Non-College').replace(/"/g, '""')}"`,
        `"${(user.status || '').replace(/"/g, '""')}"`,
        `"${(user.account_status || '').replace(/"/g, '""')}"`,
        `"${((user.Activity_Status === 'Trail' || user.Activity_Status === 'trail') ? 'Trial' : (user.Activity_Status || '')).replace(/"/g, '""')}"`,
        `"${user.start_date ? format(new Date(user.start_date), 'dd MMM yyyy') : ''}"`,
        `"${user.end_date ? format(new Date(user.end_date), 'dd MMM yyyy') : ''}"`,
        `"${user.last_study_date ? format(new Date(user.last_study_date), 'dd MMM yyyy HH:mm') : ''}"`,
        `"${(user.last_lesson || '').replace(/"/g, '""')}"`,
        `"${user.access_count_7_days || 0}"`
      ];
      csvRows.push(rowData.join(','));
    }

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);

    const dateStr = format(new Date(), 'dd-MM-yyyy');
    const collegeSanitized = (selectedCollege?.college_name || 'All-Users').replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute('download', `subscriptions_${collegeSanitized}_${dateStr}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExtendClick = (user) => {
    setSelectedUser(user);
    setExtraDays(30);
    setExtensionMode('days');
    setTargetDate('');
    setShowExtendModal(true);
  };

  const handleExtendSubmit = async () => {
    if (!selectedUser) return;
    if (extensionMode === 'days' && extraDays <= 0) return;
    if (extensionMode === 'date' && !targetDate) return;

    try {
      setUpdating(true);
      setError(null);

      let newEnd;
      let extendedDays = 0;
      if (extensionMode === 'days') {
        newEnd = new Date(); // Calculate from current date as requested
        newEnd.setDate(newEnd.getDate() + Number(extraDays));
        extendedDays = Number(extraDays);
      } else {
        newEnd = new Date(targetDate);
        extendedDays = Math.max(0, differenceInCalendarDays(newEnd, new Date()));
      }

      const response = await apiService.patch(
        `/api/user-admin/subscriptions/${selectedUser.user_id}`,
        { end_date: newEnd.toISOString() }
      );

      if (response.success) {
        setSuccessMsg(`Extended ${extendedDays} days successfully!`);
        setShowExtendModal(false);
        // Refresh current view
        fetchSubscriptions();
      } else {
        setError('Failed to extend');
      }
    } catch (err) {
      setError('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Container fluid className="py-4 add-days-left-container">
      <h2 className="mb-4">
        <FaCalendarPlus className="me-2 text-primary" />
        Manage Days Left
      </h2>

      {/* Initial View: College Tiles + Non-College Tile */}
      {selectedCollege === null && (
        <>
          <InputGroup className="mb-4">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search college by name or code..."
              value={tileSearch}
              onChange={(e) => setTileSearch(e.target.value)}
            />
          </InputGroup>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Row>
              {/* All Users Tile */}
              <Col xs={12} sm={6} md={4} lg={3} className="mb-4">
                <Card
                  className="shadow-sm h-100 text-center cursor-pointer hover-shadow admin-tile-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedCollege({ college_id: 'all', college_name: 'All Users' })}
                >
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <FaUsers size={50} className="text-primary mb-3 mx-auto" />
                    <h5>All Users</h5>
                    <p className="text-muted small">All registered users</p>
                  </Card.Body>
                </Card>
              </Col>

              {/* Non-College Tile */}
              <Col xs={12} sm={6} md={4} lg={3} className="mb-4">
                <Card
                  className="shadow-sm h-100 text-center cursor-pointer hover-shadow admin-tile-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedCollege({ college_id: null, college_name: 'Non-College / System Users' })}
                >
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <FaUserShield size={50} className="text-primary mb-3 mx-auto" />
                    <h5>Non-College / System Users</h5>
                    <p className="text-muted small">Global / System-level users</p>
                  </Card.Body>
                </Card>
              </Col>

              {/* College Tiles */}
              {filteredTiles.map(college => (
                <Col xs={12} sm={6} md={4} lg={3} key={college.college_id} className="mb-4">
                  <Card
                    className="shadow-sm h-100 text-center cursor-pointer hover-shadow admin-tile-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCollege(college)}
                  >
                    <Card.Body className="d-flex flex-column justify-content-center">
                      <FaUniversity size={50} className="text-primary mb-3 mx-auto" />
                      <h5>{college.college_name}</h5>
                      {college.college_code && (
                        <Badge bg="info" className="mx-auto">{college.college_code}</Badge>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}

      {/* Student List View (after clicking any tile) */}
      {selectedCollege !== null && (
        <>
          <Button
            variant="outline-secondary"
            className="mb-4 back-btn-custom"
            onClick={() => {
              setSelectedCollege(null);
              setSubscriptions([]);
              setTileSearch('');
              setCurrentPage(1);
            }}
          >
            <FaArrowLeft className="me-2" /> Back to College List
          </Button>

          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <h4 className="mb-0">
              Students of <strong>{selectedCollege.college_name}</strong>
            </h4>
            {subscriptions.length > 0 && (
              <Button
                variant="success"
                onClick={downloadCSV}
                className="d-flex align-items-center gap-2 csv-download-btn"
              >
                <FaDownload /> Download CSV
              </Button>
            )}
          </div>

          <Card className="border-0 shadow-sm mb-4 filter-section-card">
            <Card.Body>
              <h5 className="mb-3 text-muted small fw-bold text-uppercase">Filter Students</h5>
              <Form onSubmit={(e) => { e.preventDefault(); fetchSubscriptions(); }}>
                <Row className="g-3">
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by name..."
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Username</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by username..."
                        value={filterUserName}
                        onChange={(e) => setFilterUserName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Email</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by email..."
                        value={filterEmail}
                        onChange={(e) => setFilterEmail(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Phone Number</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by phone..."
                        value={filterPhoneNumber}
                        onChange={(e) => setFilterPhoneNumber(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Account Status</Form.Label>
                      <Form.Select
                        value={filterAccountStatus}
                        onChange={(e) => setFilterAccountStatus(e.target.value)}
                      >
                        <option value="">All Account Statuses</option>
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="rejected">Rejected</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Subscription Status</Form.Label>
                      <Form.Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="">All Subscription Statuses</option>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="pending">Pending</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3}>
                    <Form.Group>
                      <Form.Label className="small text-muted">Activity Status</Form.Label>
                      <Form.Select
                        value={filterActivityStatus}
                        onChange={(e) => setFilterActivityStatus(e.target.value)}
                      >
                        <option value="">All Activity Statuses</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Dormant">Dormant</option>
                        <option value="Trial">Trial</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6} md={4} lg={3} className="d-flex align-items-end gap-2">
                    <Button variant="primary" type="submit" className="w-100 filter-btn">
                      Apply
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="w-100 filter-btn"
                      onClick={() => {
                        setFilterName('');
                        setFilterUserName('');
                        setFilterEmail('');
                        setFilterPhoneNumber('');
                        setFilterAccountStatus('');
                        setFilterStatus('');
                        setFilterActivityStatus('');
                        // Trigger fetch with cleared params
                        setTimeout(() => {
                          const params = {};
                          if (selectedCollege && selectedCollege.college_id && selectedCollege.college_id !== 'all') {
                            params.collegeId = selectedCollege.college_id;
                          } else if (selectedCollege && selectedCollege.college_id === 'all') {
                            params.collegeId = 'all';
                          }
                          apiService.get('/api/user-admin/subscriptions', params)
                            .then(response => {
                              if (response.success && Array.isArray(response.message?.users)) {
                                setSubscriptions(response.message.users);
                              }
                            });
                        }, 0);
                      }}
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : currentItems.length === 0 ? (
            <Card className="text-center p-5">
              <h5>No students found</h5>
            </Card>
          ) : (
            <>
              <Card className="border-0 shadow-sm add-days-left-table-card mb-4">
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="align-middle mb-0 add-days-left-table">
                      <thead>
                        <tr>
                          <th className="sticky-col-idx">#</th>
                          <th className="sticky-col-name" onClick={() => requestSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Name {getSortIcon('name')}
                          </th>
                          <th onClick={() => requestSort('phone_number')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Phone Number {getSortIcon('phone_number')}
                          </th>
                          <th onClick={() => requestSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Email {getSortIcon('email')}
                          </th>
                          <th onClick={() => requestSort('user_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Username {getSortIcon('user_name')}
                          </th>
                          <th onClick={() => requestSort('college_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            College Name {getSortIcon('college_name')}
                          </th>
                          <th onClick={() => requestSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Status {getSortIcon('status')}
                          </th>
                          <th onClick={() => requestSort('account_status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Account Status {getSortIcon('account_status')}
                          </th>
                          <th onClick={() => requestSort('Activity_Status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Activity Status {getSortIcon('Activity_Status')}
                          </th>
                          <th onClick={() => requestSort('start_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Joined (Start Date) {getSortIcon('start_date')}
                          </th>
                          <th onClick={() => requestSort('end_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Expiration Date {getSortIcon('end_date')}
                          </th>
                          <th onClick={() => requestSort('last_study_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Last Study Date {getSortIcon('last_study_date')}
                          </th>
                          <th onClick={() => requestSort('last_lesson')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Last Lesson {getSortIcon('last_lesson')}
                          </th>
                          <th onClick={() => requestSort('access_count_7_days')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Access (7 Days) {getSortIcon('access_count_7_days')}
                          </th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((user, idx) => {
                          const daysLeft = getDaysLeft(user.end_date);
                          return (
                            <tr key={user.user_id}>
                              <td className="sticky-col-idx">{indexOfFirstItem + idx + 1}</td>
                              <td className="sticky-col-name"><strong>{user.name}</strong></td>
                              <td>{user.phone_number || '—'}</td>
                              <td>{user.email || '—'}</td>
                              <td>{user.user_name || '—'}</td>
                              <td>{user.college_name || 'Non-College'}</td>
                              <td>
                                <Badge className={`badge-soft-status status-${(user.status || '').toLowerCase()}`}>
                                  {user.status || '—'}
                                </Badge>
                              </td>
                              <td>
                                <Badge className={`badge-soft-status status-${(user.account_status || '').toLowerCase()}`}>
                                  {user.account_status || '—'}
                                </Badge>
                              </td>
                              <td>
                                <Badge className={`badge-soft-activity activity-${(user.Activity_Status === 'Trail' || user.Activity_Status === 'trail' ? 'trial' : (user.Activity_Status || '').toLowerCase())}`}>
                                  {(user.Activity_Status === 'Trail' || user.Activity_Status === 'trail') ? 'Trial' : (user.Activity_Status || '—')}
                                </Badge>
                              </td>
                              <td>
                                {user.start_date ? format(new Date(user.start_date), 'dd MMM yyyy') : '—'}
                              </td>
                              <td>
                                {user.end_date ? format(new Date(user.end_date), 'dd MMM yyyy') : '—'}
                                <div className="small text-muted">
                                  ({daysLeft} days left)
                                </div>
                              </td>
                              <td>
                                {user.last_study_date ? format(new Date(user.last_study_date), 'dd MMM yyyy HH:mm') : '—'}
                              </td>
                              <td>{user.last_lesson || '—'}</td>
                              <td>
                                <Badge className="badge-soft-access" bg={Number(user.access_count_7_days) > 0 ? 'info' : 'light'} text={Number(user.access_count_7_days) > 0 ? 'white' : 'dark'}>
                                  {user.access_count_7_days || 0}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  variant="link"
                                  className="extend-action-btn text-primary p-0"
                                  onClick={() => handleExtendClick(user)}
                                >
                                  <FaPlusCircle />
                                  Extend
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>

                  {sortedStudents.length > 0 && (
                    <div className="admin-chat-table-footer">
                      <div className="text-muted small">
                        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, sortedStudents.length)}
                        {" "}to{" "}
                        {Math.min(currentPage * itemsPerPage, sortedStudents.length)}
                        {" "}of {sortedStudents.length} students
                      </div>

                      <Pagination className="admin-chat-pagination mb-0">
                        <Pagination.First
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        />

                        {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => {
                          const isActive = page === currentPage;
                          const shouldShow =
                            totalPages <= 7 ||
                            page === 1 ||
                            page === totalPages ||
                            Math.abs(page - currentPage) <= 1;

                          if (!shouldShow) return null;

                          const showLeftEllipsis = page === currentPage - 1 && page > 2;
                          const showRightEllipsis = page === currentPage + 1 && page < totalPages - 1;

                          return (
                            <React.Fragment key={page}>
                              {showLeftEllipsis && <Pagination.Ellipsis disabled />}
                              <Pagination.Item active={isActive} onClick={() => handlePageChange(page)}>
                                {page}
                              </Pagination.Item>
                              {showRightEllipsis && <Pagination.Ellipsis disabled />}
                            </React.Fragment>
                          );
                        })}

                        <Pagination.Next
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </>
      )}

      {/* Extend Modal */}
      <Modal show={showExtendModal} onHide={() => setShowExtendModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Extend Subscription Days</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div>
              <p><strong>Student:</strong> {selectedUser.name}</p>
              <p><strong>Email:</strong> {selectedUser.email}</p>
              <p>
                Current End Date:{' '}
                {selectedUser.end_date ? format(new Date(selectedUser.end_date), 'dd MMM yyyy') : 'Not set'}
              </p>

              <Form.Group className="mt-3">
                <Form.Label>Extension Method</Form.Label>
                <div className="d-flex gap-3 mb-3">
                  <Form.Check
                    type="radio"
                    label="Add Days (from Today)"
                    name="extensionMode"
                    id="mode-days"
                    checked={extensionMode === 'days'}
                    onChange={() => setExtensionMode('days')}
                  />
                  <Form.Check
                    type="radio"
                    label="Select End Date"
                    name="extensionMode"
                    id="mode-date"
                    checked={extensionMode === 'date'}
                    onChange={() => setExtensionMode('date')}
                  />
                </div>

                {extensionMode === 'days' ? (
                  <>
                    <Form.Label>Days to Add (from Today)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={extraDays}
                      onChange={(e) => setExtraDays(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                  </>
                ) : (
                  <>
                    <Form.Label>Select New End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={targetDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setTargetDate(e.target.value)}
                    />
                  </>
                )}
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExtendModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleExtendSubmit} disabled={updating || (extensionMode === 'date' && !targetDate)}>
            {updating ? 'Updating...' : 'Update Subscription'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Messages */}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}
    </Container>
  );
};

export default AddDaysLeft;
